<template>
  <div class="notes-page-root">
    <div class="notes-page-sticky-shell">
      <NotesHeader
        :show-notes-toolbar="true"
        :note-flagged="noteFlagged"
        :note-read="noteReadLocal"
        :note-feedback-active="noteFeedbackActive"
        @back="returnFromNoteViaHeaderBack"
        @toggle-search="toggleSearchPanel"
        @zoom-in="zoomContent(0.08)"
        @zoom-out="zoomContent(-0.08)"
        @toggle-flag="toggleToolbarFlag"
        @toggle-read="toggleToolbarRead"
        @open-feedback="openNoteFeedbackModal"
      />

      <div class="notes-page-sticky-chrome">
      <section class="question-breadcrum notes-breadcrum">
        <div class="container">
          <div class="breadcrumb">
            <span class="breadcrumb-item" @click="goToNotesList">MLA content map</span>

             <template v-if="notesViewModeLabel">
              <span class="breadcrumb-arrow">›</span>
              <span class="breadcrumb-item" @click="returnToNotesListView">{{ notesViewModeLabel }}</span>
            </template>
            
            <template v-if="notesSection">
              <span class="breadcrumb-arrow">›</span>
              <span class="breadcrumb-item" @click="goToNotesList">{{ notesSection }}</span>
            </template>

           

            <template v-if="notesParentTitle">
              <span class="breadcrumb-arrow">›</span>
              <span class="breadcrumb-item" @click="returnFromNoteViaBreadcrumb">{{ notesParentTitle }}</span>
            </template>

            <template v-if="notesChildTitle">
              <span class="breadcrumb-arrow">›</span>
              <span class="breadcrumb-item" @click="returnFromNoteViaBreadcrumb">{{ notesChildTitle }}</span>
            </template>

            <template v-if="noteTitle">
              <span class="breadcrumb-arrow">›</span>
              <span class="breadcrumb-item active">{{ noteTitle }}</span>
            </template>
          </div>
        </div>
      </section>

      <div class="container">
        <h1 v-if="noteTitle" class="note-page-main-heading">
          <template v-if="notesChildTitle">{{ notesChildTitle }} — </template>{{ noteTitle }}<template v-if="noteNumber"> - {{ noteNumber }}</template>
        </h1>
      </div>
      </div>
    </div>

    <section class="notes-page-sec">
      <div class="container">
        <div class="notes-content-column">
          <div
            ref="noteBodyRoot"
            class="notes-editor-container"
          >
            <div v-if="showSearchPanel" class="notes-search-panel" @click.stop>
              <input
                v-model="searchInput"
                type="search"
                class="notes-search-input"
                placeholder="Find"
                @keydown.enter.prevent="runSearchHighlight"
              />
              <button type="button" class="notes-search-btn" @click="runSearchHighlight">Highlight</button>
              <button type="button" class="notes-search-clear" @click="clearSearchMarks">Clear</button>
            </div>
            <div class="editor-wrapper">
              <div
                class="note-html-content"
                :style="editorZoomStyle"
              >
                <div
                  ref="noteHtmlBody"
                  class="note-html-body"
                  v-html="editorContent"
                  @contextmenu="onNoteContentContextMenu"
                ></div>
              </div>
            </div>
          </div>

          <!-- Per-user text highlight menu (does not mutate shared note content) -->
          <div
            v-if="highlightMenu.visible"
            class="note-highlight-menu"
            :style="{ top: `${highlightMenu.y}px`, left: `${highlightMenu.x}px` }"
            @click.stop
            @contextmenu.prevent
          >
            <template v-if="highlightMenu.pendingRange">
              <div class="note-highlight-menu-title">Highlight</div>
              <div class="note-highlight-swatches">
                <button
                  v-for="c in highlightColors"
                  :key="c.id"
                  type="button"
                  class="note-highlight-swatch"
                  :title="c.label"
                  :aria-label="c.label"
                  :style="{ backgroundColor: c.value }"
                  @click="applyHighlightColor(c.value)"
                />
              </div>
            </template>
            <button
              v-if="highlightMenu.existingId"
              type="button"
              class="note-highlight-remove"
              :class="{ 'note-highlight-remove--solo': !highlightMenu.pendingRange }"
              @click="removeExistingHighlight"
            >
              Remove highlight
            </button>
          </div>

          <div
            v-if="navSequence.length > 0"
            class="questionsoptions-arrows no-submit notes-nav-arrows"
          >
            <div
              class="questionoption-leftarrow"
              :class="{ 'questionoption-arrow--disabled': !canGoPrevious }"
              title="Previous note"
              @click="goPreviousNote"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                stroke-linejoin="round">
                <path d="m15 18-6-6 6-6" />
              </svg>
            </div>
            <div
              class="questionoption-rightarrow"
              :class="{ 'questionoption-arrow--disabled': !canGoNext }"
              title="Next note"
              @click="goNextNote"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                stroke-linejoin="round">
                <path d="m9 18 6-6-6-6" />
              </svg>
            </div>
          </div>

          <div class="brake-border"></div>
          <NotesLinkedQuestions
            :questions="linkedQuestions"
            :questions-loading="questionsLoading"
            :linked-notes="[]"
            :notes-loading="false"
            :mock-questions="linkedMocks"
            :mocks-loading="mocksLoading"
            :title="noteTitle"
            :note-code="noteNumber"
            :note-name="noteTitle"
            :note-record-id="noteRecordId"
            :note-record-type="noteRecordType"
            comment-api="note"
            @question-click="navigateToQuestion"
            @mock-click="navigateToMockQuestion"
          />
        </div>
      </div>

      <!-- Feedback: text only -->
      <transition name="notes-slide-modal">
        <div v-if="showFeedbackModal" class="notes-modal-overlays">
          <div class="notes-modal-contents">
            <div class="notes-feedback-form-box notes-feedback-form-box--simple">
              <div class="notes-feedback-cross feedback-modal-header">
                <h4 class="feedback-modal-title">Feedback</h4>
                <span class="notes-crossspan feedback-modal-close" @click="showFeedbackModal = false">
                  <svg width="12" height="12" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1.38013 0.75L8.1701 7.54001" stroke="#6D6E71" stroke-miterlimit="10" stroke-linecap="round" />
                    <path d="M8.1701 0.75L1.38013 7.54001" stroke="#6D6E71" stroke-miterlimit="10" stroke-linecap="round" />
                  </svg>
                </span>
              </div>
              <form @submit.prevent="submitNoteFeedback">
                <div
                  v-for="(category, index) in noteFeedbackCategories"
                  :key="index"
                  class="notes-feedbackform-button"
                >
                  <button
                    type="button"
                    :class="{ 'notes-fb-active-btn': category.selectedOption }"
                    @click="toggleNoteFeedbackOptions(index)"
                  >{{ category.name }}</button>
                  <div class="notes-feeback-options" v-if="noteFeedbackShowOptionsIndex === index">
                    <div class="notes-feeback-option" @click="selectNoteFeedbackOption(index, 'Incorrect')">
                      <input type="radio" :checked="category.selectedOption === 'Incorrect'" value="Incorrect" readonly tabindex="-1" />
                      <p :class="{ 'notes-fb-active-btn': category.selectedOption === 'Incorrect' }">Incorrect</p>
                    </div>
                    <div class="notes-feeback-option" @click="selectNoteFeedbackOption(index, 'Needs improvement')">
                      <input type="radio" :checked="category.selectedOption === 'Needs improvement'" value="Needs improvement" readonly tabindex="-1" />
                      <p :class="{ 'notes-fb-active-btn': category.selectedOption === 'Needs improvement' }">Needs improvement</p>
                    </div>
                  </div>
                </div>
                <div class="notes-feedback-textarea-box notes-feedback-textarea-box--full">
                  <textarea
                    ref="noteFeedbackArea"
                    v-model="feedbackForm.optionfeedback"
                    class="notes-feedback-textarea notes-feedback-textarea--tall"
                    rows="1"
                    placeholder="Your feedback..."
                    @input="onNoteFeedbackTextareaInput"
                  />
                </div>
                <div class="notes-feedbackform-submitbtn">
                  <button
                    type="submit"
                    :disabled="isNoteFeedbackSubmitDisabled"
                    :class="{ 'notes-disabled-btn': isNoteFeedbackSubmitDisabled }"
                  >Submit</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </transition>
    </section>
  </div>
</template>

<script>
import NotesHeader from '@/components/NotesHeader.vue';
import NotesLinkedQuestions from '@/components/NotesLinkedQuestions.vue';
import { get, byMethod } from './lib/api';
import {
  autoGrowFeedbackTextarea,
  resetFeedbackTextareaHeight,
} from './lib/feedbackTextareaAutoGrow';
import {
  markNoteVisitedAsRead,
  markNoteUnread,
  isRecordRead,
  isRecordFlagged,
  fetchFlagged,
  toggleRecordFlag,
  feedbackHasContent,
  applyFeedbackToCache,
  hydrateNoteUserState,
  getNoteHighlights,
  addNoteHighlight,
  removeNoteHighlight,
  registerNoteRecord,
} from './lib/notesUserPrefs';
import {
  NOTE_HIGHLIGHT_COLORS,
  createHighlightId,
  rangeToOffsets,
  applyHighlightsToContainer,
  getSelectionRangeInContainer,
  findHighlightIdFromNode,
  removeHighlightById,
} from './lib/notesTextHighlight';
import { requestCloseEmbeddedPreview } from './lib/embeddedPreview';
import {
  fetchNotesNavSequence,
  resolveNotesNavContext,
  persistNotesNavRestoreState,
  findNavIndex,
} from './lib/notesNavigation';

export default {
  name: 'NotesPage',
  components: {
    NotesHeader,
    NotesLinkedQuestions,
  },
  data() {
    return {
      showBreadcrumb: false,
      loading: true,
      editorContent: '',
      noteTitle: '',
      noteNumber: '',

      notesSection: '',
      notesViewMode: '',
      notesParentTitle: '',
      notesChildTitle: '',
      linkedQuestions: [],
      questionsLoading: true,
      linkedMocks: [],
      mocksLoading: true,
      noteFlagged: false,
      readRev: 0,
      contentZoom: 1,
      showSearchPanel: false,
      searchInput: '',
      showFeedbackModal: false,
      feedbackForm: { optionfeedback: '' },
      noteFeedbackCategories: [
        { name: 'Notes',       selectedOption: null },
        { name: 'Visual aids', selectedOption: null },
      ],
      noteFeedbackShowOptionsIndex: null,
      navSequence: [],
      navCurrentIndex: -1,
      navContext: { columnKey: 'clinical', viewMode: 'areas' },
      highlightColors: NOTE_HIGHLIGHT_COLORS,
      highlightMenu: {
        visible: false,
        x: 0,
        y: 0,
        existingId: null,
        pendingRange: null,
      },
    };
  },
  created() {
    this.noteTitle       = localStorage.getItem('notestitle')     || 'Notes';

    this.notesSection    = localStorage.getItem('notesSection')   || '';
    this.notesViewMode   = localStorage.getItem('notesViewMode')  || 'areas';
    this.notesParentTitle = localStorage.getItem('notesParentTitle') || '';
    this.notesChildTitle  = localStorage.getItem('notesChildTitle')  || '';

    this.loadNoteFromRoute();
    this.loadNavSequence();
    hydrateNoteUserState();
  },
  mounted() {
    document.addEventListener('click', this.closeHighlightMenu);
    document.addEventListener('scroll', this.closeHighlightMenu, true);
    window.addEventListener('resize', this.closeHighlightMenu);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeHighlightMenu);
    document.removeEventListener('scroll', this.closeHighlightMenu, true);
    window.removeEventListener('resize', this.closeHighlightMenu);
  },
  watch: {
    '$route.query': {
      handler(newQ, oldQ) {
        const idChanged = String(newQ?.id || '') !== String(oldQ?.id || '');
        const typeChanged = (newQ?.type || 'notes') !== (oldQ?.type || 'notes');
        if (idChanged || typeChanged) {
          this.closeHighlightMenu();
          this.noteTitle = localStorage.getItem('notestitle') || this.noteTitle || 'Notes';
          this.notesParentTitle = localStorage.getItem('notesParentTitle') || '';
          this.notesChildTitle = localStorage.getItem('notesChildTitle') || '';
          this.loadNoteFromRoute();
          this.navCurrentIndex = findNavIndex(
            this.navSequence,
            newQ?.id,
            newQ?.type || 'notes',
          );
        }
        this.syncNoteReadAndFlag();
      },
      deep: true,
    },
    editorContent() {
      this.$nextTick(() => {
        this.clearSearchMarks();
        this.restoreUserHighlights();
      });
    },
  },
  computed: {
    isPageLoading() {
      return this.loading || this.questionsLoading || this.mocksLoading;
    },
    noteRecordId() {
      return this.$route.query.id || '';
    },
    noteRecordType() {
      const raw = this.$route.query.type || localStorage.getItem('notestype') || 'notes';
      return raw === 'subnotes' ? 'subnotes' : 'notes';
    },
    editorZoomStyle() {
      // 14.7px == Word's 11pt body text, the same base the admin editor renders at.
      // List gutter and indent step are emitted as px so they stay identical to the
      // admin editor no matter what font-size the indented element itself has.
      const z = this.contentZoom;
      return {
        fontSize: `${14.7 * z}px`,
        '--note-list-indent': `${18 * z}px`,
        '--note-indent-step': `${19 * z}px`,
      };
    },
    noteReadLocal() {
      void this.readRev;
      const id = this.noteRecordId;
      if (!id) return false;
      return isRecordRead(this.$store, this.noteRecordType, id, this.noteNumber);
    },
    isNoteFeedbackSubmitDisabled() {
      const hasText = (this.feedbackForm.optionfeedback || '').trim().length > 0;
      const hasCategory = this.noteFeedbackCategories.some(c => c.selectedOption);
      return !hasText && !hasCategory;
    },
    noteFeedbackActive() {
      const id = this.noteRecordId;
      if (!id) return false;
      return feedbackHasContent(this.$store, this.noteRecordType, id);
    },
    notesViewModeLabel() {
      return this.notesViewMode === 'conditions' ? 'Conditions' : 'Areas';
    },
    canGoPrevious() {
      return this.navCurrentIndex > 0;
    },
    canGoNext() {
      return this.navSequence.length > 0 && this.navCurrentIndex < this.navSequence.length - 1;
    },
  },
  methods: {
    loadNoteFromRoute() {
      const noteId = this.$route.query.id;
      const noteType = this.$route.query.type || localStorage.getItem('notestype') || 'notes';

      if (noteId) {
        this.fetchNoteContent(noteId, noteType);
        this.fetchLinkedQuestions(noteId, noteType);
        this.fetchLinkedMocks(noteId, noteType);
      }
      this.syncNoteReadAndFlag();
    },

    async loadNavSequence() {
      const noteId = this.$route.query.id;
      const noteType = this.$route.query.type || localStorage.getItem('notestype') || 'notes';
      if (!noteId) {
        this.navSequence = [];
        this.navCurrentIndex = -1;
        return;
      }

      const ctx = resolveNotesNavContext();
      this.navContext = ctx;
      this.notesViewMode = ctx.viewMode;

      try {
        const result = await fetchNotesNavSequence({
          columnKey: ctx.columnKey,
          viewMode: ctx.viewMode,
          noteId,
          noteType,
        });
        this.navSequence = result.sequence || [];
        this.navCurrentIndex = result.index;
        if (result.columnKey) {
          this.navContext = { columnKey: result.columnKey, viewMode: result.viewMode };
        }
      } catch (e) {
        this.navSequence = [];
        this.navCurrentIndex = -1;
      }
    },

    applyNavItem(item) {
      if (!item) return;

      persistNotesNavRestoreState(item, this.navContext);
      this.noteTitle = item.title || 'Notes';
      this.notesParentTitle = item.parentTitle || '';
      this.notesChildTitle = item.childTitle || '';

      const query = {
        id: item.id,
        type: item.type,
      };
      if (this.$route.query.from) {
        query.from = this.$route.query.from;
      }

      this.$router.push({ path: '/notespage', query });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    goPreviousNote() {
      if (!this.canGoPrevious) return;
      const item = this.navSequence[this.navCurrentIndex - 1];
      this.applyNavItem(item);
    },

    goNextNote() {
      if (!this.canGoNext) return;
      const item = this.navSequence[this.navCurrentIndex + 1];
      this.applyNavItem(item);
    },
    async syncNoteReadAndFlag() {
      const id = this.$route.query.id;
      const rawType = this.$route.query.type || localStorage.getItem('notestype') || 'notes';
      const t = rawType === 'subnotes' ? 'subnotes' : 'notes';
      if (id != null && id !== '') {
        this.readRev += 1;
        try {
          this.noteFlagged = await fetchFlagged(this.$store, t, id);
        } catch (e) {
          this.noteFlagged = isRecordFlagged(this.$store, t, id);
        }
      } else {
        this.noteFlagged = false;
      }
    },

    async toggleToolbarFlag() {
      const id = this.$route.query.id;
      const rawType = this.$route.query.type || localStorage.getItem('notestype') || 'notes';
      const t = rawType === 'subnotes' ? 'subnotes' : 'notes';
      if (id == null || id === '') return;
      try {
        if (this.noteNumber) registerNoteRecord(t, id, this.noteNumber);
        this.noteFlagged = await toggleRecordFlag(this.$store, t, id, this.noteNumber);
      } catch (e) {
        /* ignore */
      }
    },

    toggleToolbarRead() {
      const id = this.$route.query.id;
      const t = this.noteRecordType;
      if (id == null || id === '') return;
      if (this.noteNumber) registerNoteRecord(t, id, this.noteNumber);
      if (this.noteReadLocal) {
        markNoteUnread(this.$store, id, t, this.noteNumber);
      } else {
        markNoteVisitedAsRead(this.$store, id, t, this.noteNumber);
      }
      this.readRev += 1;
    },

    toggleSearchPanel() {
      this.showSearchPanel = !this.showSearchPanel;
      if (!this.showSearchPanel) {
        this.clearSearchMarks();
      }
    },

    zoomContent(delta) {
      const next = Math.min(1.45, Math.max(0.85, this.contentZoom + delta));
      this.contentZoom = Math.round(next * 100) / 100;
    },

    escapeRegExp(str) {
      return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    },

    clearSearchMarks() {
      const root = this.$refs.noteBodyRoot;
      if (!root) return;
      root.querySelectorAll('mark.note-search-hit').forEach((mark) => {
        const parent = mark.parentNode;
        if (!parent) return;
        parent.replaceChild(document.createTextNode(mark.textContent || ''), mark);
        parent.normalize();
      });
    },

    getNoteBodyEl() {
      return this.$refs.noteHtmlBody
        || (this.$refs.noteBodyRoot && this.$refs.noteBodyRoot.querySelector('.note-html-body'));
    },

    closeHighlightMenu() {
      if (!this.highlightMenu.visible) return;
      this.highlightMenu.visible = false;
      this.highlightMenu.existingId = null;
      this.highlightMenu.pendingRange = null;
    },

    positionHighlightMenu(clientX, clientY) {
      const menuW = 200;
      const menuH = 110;
      const pad = 8;
      let x = clientX;
      let y = clientY;
      if (x + menuW > window.innerWidth - pad) x = window.innerWidth - menuW - pad;
      if (y + menuH > window.innerHeight - pad) y = window.innerHeight - menuH - pad;
      if (x < pad) x = pad;
      if (y < pad) y = pad;
      this.highlightMenu.x = x;
      this.highlightMenu.y = y;
    },

    onNoteContentContextMenu(e) {
      const body = this.getNoteBodyEl();
      if (!body) return;

      const existingId = findHighlightIdFromNode(e.target, body);
      const range = getSelectionRangeInContainer(body);

      if (!range && !existingId) {
        this.closeHighlightMenu();
        return;
      }

      e.preventDefault();

      // Snapshot selection offsets now — Selection may clear when menu is clicked
      let pendingRange = null;
      if (range) {
        const offsets = rangeToOffsets(body, range);
        if (offsets) {
          pendingRange = {
            start: offsets.start,
            end: offsets.end,
            text: range.toString(),
          };
        }
      }

      if (!pendingRange && !existingId) {
        this.closeHighlightMenu();
        return;
      }

      this.highlightMenu.pendingRange = pendingRange;
      this.highlightMenu.existingId = existingId || null;
      this.positionHighlightMenu(e.clientX, e.clientY);
      this.highlightMenu.visible = true;
    },

    applyHighlightColor(color) {
      const body = this.getNoteBodyEl();
      const id = this.noteRecordId;
      if (!body || !id || !this.highlightMenu.pendingRange) {
        this.closeHighlightMenu();
        return;
      }

      const { start, end, text } = this.highlightMenu.pendingRange;
      const highlightId = createHighlightId();
      addNoteHighlight(this.$store, this.noteRecordType, id, {
        id: highlightId,
        start,
        end,
        color,
        text: text || '',
      });
      this.closeHighlightMenu();
      this.restoreUserHighlights();

      const sel = window.getSelection && window.getSelection();
      if (sel) sel.removeAllRanges();
    },

    removeExistingHighlight() {
      const body = this.getNoteBodyEl();
      const hid = this.highlightMenu.existingId;
      const id = this.noteRecordId;
      if (!body || !hid || !id) {
        this.closeHighlightMenu();
        return;
      }
      removeHighlightById(body, hid);
      removeNoteHighlight(this.$store, this.noteRecordType, id, hid);
      this.closeHighlightMenu();
    },

    restoreUserHighlights() {
      const body = this.getNoteBodyEl();
      const id = this.noteRecordId;
      if (!body || !id) return;
      const list = getNoteHighlights(this.$store, this.noteRecordType, id);
      applyHighlightsToContainer(body, list);
    },

    runSearchHighlight() {
      this.clearSearchMarks();
      const q = (this.searchInput || '').trim();
      if (!q) return;
      const root = this.$refs.noteBodyRoot;
      if (!root) return;
      const contentEl = root.querySelector('.note-html-body');
      if (!contentEl) return;

      const textNodes = [];
      const walk = document.createTreeWalker(contentEl, NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
          let p = node.parentNode;
          while (p && p !== contentEl) {
            if (p.nodeName === 'MARK' && p.classList && p.classList.contains('note-search-hit')) {
              return NodeFilter.FILTER_REJECT;
            }
            p = p.parentNode;
          }
          return NodeFilter.FILTER_ACCEPT;
        },
      });
      let n;
      while ((n = walk.nextNode())) {
        if (n.nodeValue && n.nodeValue.trim()) textNodes.push(n);
      }

      const re = new RegExp(`(${this.escapeRegExp(q)})`, 'gi');
      let hits = 0;
      const maxHits = 120;

      for (let i = 0; i < textNodes.length && hits < maxHits; i += 1) {
        const textNode = textNodes[i];
        const text = textNode.nodeValue;
        if (!text || !re.test(text)) {
          re.lastIndex = 0;
          continue;
        }
        re.lastIndex = 0;
        const parts = text.split(re);
        if (parts.length < 2) continue;

        const frag = document.createDocumentFragment();
        parts.forEach((part, idx) => {
          if (part === '' || part == null) return;
          if (idx % 2 === 1 && hits < maxHits) {
            const m = document.createElement('mark');
            m.className = 'note-search-hit';
            m.textContent = part;
            frag.appendChild(m);
            hits += 1;
          } else {
            frag.appendChild(document.createTextNode(part));
          }
        });
        textNode.parentNode.replaceChild(frag, textNode);
      }

      const first = root.querySelector('mark.note-search-hit');
      if (first && typeof first.scrollIntoView === 'function') {
        first.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    },

    toggleNoteFeedbackOptions(index) {
      this.noteFeedbackShowOptionsIndex = this.noteFeedbackShowOptionsIndex === index ? null : index;
    },

    selectNoteFeedbackOption(index, option) {
      const cats = [...this.noteFeedbackCategories];
      cats[index] = {
        ...cats[index],
        selectedOption: cats[index].selectedOption === option ? null : option,
      };
      this.noteFeedbackCategories = cats;
    },

    resetNoteFeedbackForm() {
      this.feedbackForm.optionfeedback = '';
      this.noteFeedbackCategories = this.noteFeedbackCategories.map(cat => ({ ...cat, selectedOption: null }));
      this.noteFeedbackShowOptionsIndex = null;
    },

    async openNoteFeedbackModal() {
      const id = this.noteRecordId;
      if (!id) return;
      this.resetNoteFeedbackForm();
      try {
        const res = await get('/note-feedback', { id, type: this.noteRecordType });
        const d = res.data?.data;
        if (d && d.optionfeedback) {
          this.feedbackForm.optionfeedback = d.optionfeedback;
        }
        if (d && d.categories) {
          this.noteFeedbackCategories = this.noteFeedbackCategories.map(cat => ({
            ...cat,
            selectedOption: d.categories[cat.name] || null,
          }));
        }
      } catch (e) {
        /* ignore */
      }
      this.showFeedbackModal = true;
      this.$nextTick(() => {
        resetFeedbackTextareaHeight(this.$refs.noteFeedbackArea);
      });
    },

    onNoteFeedbackTextareaInput() {
      autoGrowFeedbackTextarea(this.$refs.noteFeedbackArea);
      this.noteFeedbackShowOptionsIndex = null;
    },

    submitNoteFeedback() {
      const id = this.noteRecordId;
      if (!id) return;
      const selectedCategories = {};
      this.noteFeedbackCategories.forEach(cat => {
        if (cat.selectedOption) selectedCategories[cat.name] = cat.selectedOption;
      });
      const payload = {
        id,
        type: this.noteRecordType,
        optionfeedback: (this.feedbackForm.optionfeedback || '').trim(),
        categories: selectedCategories,
      };
      byMethod('POST', '/note-feedback', payload)
        .then((res) => {
          if (res.data.saved) {
            applyFeedbackToCache(this.noteRecordType, this.noteRecordId, true);
            this.showFeedbackModal = false;
          }
        })
        .catch(() => {
          /* ignore */
        });
    },

    clearNotesListReturnState() {
      try {
        [
          'notesPendingHighlightId',
          'notesRestore_column',
          'notesRestore_parentIndex',
          'notesRestore_childIndex',
          'notesRestore_grandIndex',
          'notesListRestoreSection',
          'notesListRestoreViewMode',
          'notesSuppressListHighlight',
        ].forEach((k) => localStorage.removeItem(k));
      } catch (e) {
        /* ignore */
      }
    },

    goToNotesList() {
      this.clearNotesListReturnState();
      this.$router.push('/noteslistselection');
    },

    /**
     * Header back: same chapter/sublist expanded + orange outline on the note row only.
     * When opened from Search, return to that search URL instead.
     */
    returnFromNoteViaHeaderBack() {
      if (requestCloseEmbeddedPreview()) return;
      const from = this.$route.query.from;
      if (from && typeof from === 'string' && from.startsWith('/')) {
        this.$router.push(from);
        return;
      }
      const id = this.$route.query.id;
      try {
        localStorage.removeItem('notesSuppressListHighlight');
        if (id != null && id !== '') {
          localStorage.setItem('notesPendingHighlightId', String(id));
        }
      } catch (e) {
        /* ignore */
      }
      this.$router.push('/noteslistselection');
    },

    /**
     * Chapter / mid-tier breadcrumb: expand tree back on the list but do not outline a row.
     */
    returnToNotesListView() {
      try {
        localStorage.removeItem('notesPendingHighlightId');
        localStorage.setItem('notesSuppressListHighlight', '1');
        const vm = this.notesViewMode || 'areas';
        localStorage.setItem('notesListRestoreSection', vm === 'conditions' ? 'BY CONDITIONS' : 'BY AREAS');
        localStorage.setItem('notesListRestoreViewMode', vm);
      } catch (e) {
        /* ignore */
      }
      this.$router.push('/noteslistselection');
    },

    returnFromNoteViaBreadcrumb() {
      this.returnToNotesListView();
    },
    /**
     * Navigate to QuestionsPage, landing directly on the clicked question.
     * Priority: sublist > condition > presentation > subject (chapter).
     */
    navigateToQuestion(que) {
      let title, entityId;

      if (que.sublist_id) {
        title    = 'Sublist';
        entityId = que.sublist_id;
      } else if (que.condition_id) {
        title    = 'Conditions';
        entityId = que.condition_id;
      } else if (que.presentation_id) {
        title    = 'Presentations';
        entityId = que.presentation_id;
      } else {
        title    = 'Chapter';
        entityId = que.subject_id;
      }

      localStorage.setItem('questiontitle', title);
      localStorage.setItem('questionStartCode', que.code);
      const noteTitle = this.noteTitle || 'Notes';
      try {
        localStorage.setItem(
          'questionBreadcrumbLabels',
          JSON.stringify(['MLA CONTENT MAP', 'Notes', noteTitle])
        );
      } catch (e) { /* ignore */ }

      this.$router.push({ name: 'QuestionsPage', params: { id: entityId } });
    },
    /**
     * CMS sometimes saves invalid markup like <ul><ul><ul><li>...</li></ul></ul></ul>.
     * Each extra <ul> adds a full list-indent; unwrap so one <ul> wraps the items.
     */
    normalizeRedundantListWrappers(html) {
      if (!html || typeof html !== 'string') {
        return html;
      }
      let out = html;
      let prev;
      do {
        prev = out;
        out = out
          .replace(/<ul>\s*<ul>/gi, '<ul>')
          .replace(/<\/ul>\s*<\/ul>/gi, '</ul>')
          .replace(/<ol>\s*<ol>/gi, '<ol>')
          .replace(/<\/ol>\s*<\/ol>/gi, '</ol>');
      } while (out !== prev);
      return out;
    },
    fetchLinkedMocks(id, type) {
      this.mocksLoading = true;
      get('/note-linked-mocks', { id, type })
        .then((res) => {
          this.linkedMocks = res.data.data || [];
        })
        .catch((error) => {
          console.error('Error fetching linked mocks:', error);
          this.linkedMocks = [];
        })
        .finally(() => {
          this.mocksLoading = false;
        });
    },
    /**
     * Navigate to MockQuestionPreview (view-only) landing on the clicked question.
     */
    navigateToMockQuestion(que) {
      localStorage.setItem('question', 'normal');
      localStorage.setItem('mockObject', JSON.stringify({
        id: que.mock_id,
        title: que.mock_title,
        duration: que.mock_duration || 0,
      }));
      localStorage.setItem('mockStartQuestionId', String(que.id));
      this.$router.push({ name: 'MockQuestionPreview', params: { id: que.mock_id } });
    },
    fetchLinkedQuestions(id, type) {
      this.questionsLoading = true;
      get('/note-linked-questions', { id, type })
        .then((res) => {
          this.linkedQuestions = res.data.data || [];
        })
        .catch((error) => {
          console.error('Error fetching linked questions:', error);
          this.linkedQuestions = [];
        })
        .finally(() => {
          this.questionsLoading = false;
        });
    },
    fetchNoteContent(noteId, type) {
      this.loading = true;
      
      // Call API with id and type parameters
      get('/getnotecontent', {
        id: noteId,
        type: type
      })
      .then((res) => {
        if (res.data && res.data.content) {
          const raw = res.data.content?.content;

          this.noteNumber = res.data.number ?? ''
          if (this.noteNumber && noteId != null) {
            registerNoteRecord(type === 'subnotes' ? 'subnotes' : 'notes', noteId, this.noteNumber);
          }
          this.editorContent = this.normalizeRedundantListWrappers(raw);
        } else {
          this.editorContent = '<p>No content available.</p>';
        }
        this.loading = false;
      })
      .catch((error) => {
        console.error('Error fetching note content:', error);
        this.editorContent = '<p>Error loading content. Please try again.</p>';
        this.loading = false;
      });
    }
  }
};
</script>

<style scoped>
.notes-page-root {
  background: #fff;
}

.notes-page-sticky-shell {
  position: sticky;
  top: 0;
  z-index: 25;
  background: #fff;
 
}

.notes-page-sticky-chrome {
  background: #fff;
  text-align: left;
}

.notes-page-sticky-chrome .container {
  text-align: left;
}

.notes-breadcrum .container .breadcrumb {
  padding: 10px 0 0 0;
  height: auto;
  min-height: 0;
  margin: 0;
}

.notes-page-sticky-chrome .note-page-main-heading {
  margin: 20px 30px 12px;
  padding: 0;
  font-size: 1.75rem;
  font-weight: 700;
  color: #000;
  line-height: 1.25;
  text-align: left;
  display: block;
  width: 100%;
}

.notes-page-sec {
  padding: 0 0 20px;
  min-height: calc(100vh - 200px);
}

.notes-editor-container {
  width: 100%;
  min-height: calc(100vh - 300px);
}

.notes-content-column {
  flex: 1;
  min-width: 0;
  width: 100%;
}

.notes-breadcrum .breadcrumb-item {
  padding: 3px 10px;
  color: white;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 300;
  cursor: pointer;
  background: #BBBDBF;
}

.notes-breadcrum .breadcrumb-item.active {
  background-color: #faad1b;
  color: white;
  cursor: default;
}

.notes-breadcrum .breadcrumb-item:hover {
  background-color: #FADE91;
  color: #231F20;
}

.notes-breadcrum .breadcrumb-arrow {
  padding: 0 6px;
  color: #BBBDBF;
  font-size: 16px;
}

.notes-search-panel {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  margin-bottom: 10px;
  background: #f8f8f8;
  border-radius: 10px;
}

.notes-search-input {
  flex: 1;
  min-width: 160px;
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 14px;
}

.notes-search-btn,
.notes-search-clear {
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #fbad18;
  background: #fbad18;
  color: #fff;
  font-size: 13px;
  cursor: pointer;
}

.notes-search-clear {
  background: #fff;
  color: #333;
  border-color: #ccc;
}

.note-html-content :deep(mark.note-search-hit) {
  background: #fff59d;
  color: #000;
  padding: 0 2px;
  border-radius: 2px;
}

.note-html-content :deep(mark.note-user-highlight) {
  color: #000;
  padding: 0 1px;
  border-radius: 2px;
  cursor: text;
}

.note-highlight-menu {
  position: fixed;
  z-index: 10050;
  min-width: 168px;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.14);
  text-align: left;
}

.note-highlight-menu-title {
  font-size: 12px;
  font-weight: 600;
  color: #555;
  margin-bottom: 8px;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.note-highlight-swatches {
  display: flex;
  gap: 8px;
  align-items: center;
}

.note-highlight-swatch {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.15);
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
}

.note-highlight-swatch:hover {
  transform: scale(1.12);
  border-color: rgba(0, 0, 0, 0.35);
}

.note-highlight-remove {
  display: block;
  width: 100%;
  margin-top: 10px;
  padding: 6px 8px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #fafafa;
  color: #333;
  font-size: 13px;
  cursor: pointer;
}

.note-highlight-remove--solo {
  margin-top: 0;
}

.note-highlight-remove:hover {
  background: #f0f0f0;
}

.notes-modal-overlays {
  width: 100%;
}

.notes-modal-contents {
  position: fixed;
  right: 38%;
  top: 22%;
  z-index: 9999;
}

.notes-slide-modal-enter-active,
.notes-slide-modal-leave-active {
  transition: opacity 0.25s ease;
}

.notes-slide-modal-enter,
.notes-slide-modal-leave-to {
  opacity: 0;
}

@media (max-width: 768px) {
  .notes-page-sticky-chrome .note-page-main-heading {
    font-size: 1.35rem;
    margin: 6px 0 10px;
  }

  .notes-modal-contents {
    right: 10%;
    left: 10%;
    top: 15%;
  }
}

.editor-wrapper {
  width: 100%;
  height: 100%;
  background: white;
  border-radius: 20px;
  padding: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

/*
  Match Laravel/admin WYSIWYG: black text, left alignment, consistent list gutters.
  #app uses text-align: center and color #2c3e50 — override here for note body.
  Sizes are in em so the zoom buttons (inline font-size on this element) keep working;
  the em ratios reproduce the admin editor's px values on its 14.7px base.
*/
.note-html-content {
  /* Overridden with zoom-scaled px by editorZoomStyle; these are the 100% values. */
  --note-list-indent: 12px;
  --note-indent-step: 19px;
  --note-indent: 0px;
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
  text-align: left;
  color: #000;
  padding: 20px;
  line-height: 1.28;
}

.note-page-main-heading {
  margin: 0 0 1.25rem;
  font-size: 1.75rem;
  font-weight: 700;
  color: #000;
  line-height: 1.25;
  text-align: left;
}

/* Vertical rhythm comes from the imported blank paragraphs, exactly like the admin editor */
.note-html-content :deep(p),
.note-html-content :deep(li),
.note-html-content :deep(h1),
.note-html-content :deep(h2),
.note-html-content :deep(h3),
.note-html-content :deep(h4),
.note-html-content :deep(h5),
.note-html-content :deep(h6) {
  font-family: inherit;
  margin: 0;
  padding-top: 0;
  padding-bottom: 0;
  line-height: 1.28;
  color: #000;
}

/* Word "Heading 1" — 14pt bold */
.note-html-content :deep(h3) {
  font-size: 1.2721em;
  font-weight: 700;
}

/* Word "Subheading Tier 1" / bold lead-in lines — 11pt bold */
.note-html-content :deep(h4) {
  font-size: 1em;
  font-weight: 700;
}

/* Word "Subheading Tier 2" — 11pt bold */
.note-html-content :deep(h5) {
  font-size: 1em;
  font-weight: 700;
}

.note-html-content :deep(td),
.note-html-content :deep(th) {
  color: #000;
}

.note-html-content :deep(a) {
  color: #000;
}

.note-html-content :deep(ul),
.note-html-content :deep(ol) {
  margin: 0;
  padding-left: var(--note-list-indent);
  max-width: 100%;
  box-sizing: border-box;
  list-style-position: outside;
  color: #000;
}

/*
  Quill paints bullets with an inline ::before, so they follow the row's indent
  padding. A native "outside" marker ignores that padding and would be stranded
  at the left margin, so reproduce Quill's marker metrics instead.
*/
.note-html-content :deep(ul > li) {
  list-style-type: none;
}

.note-html-content :deep(ul > li::before) {
  content: "\2022";
  display: inline-block;
  white-space: nowrap;
  width: 12px;
  margin-left: -12px;
  margin-right: 0;
  text-align: left;
}

.note-html-content :deep(ol) {
  list-style-type: decimal;
}

/* Legacy notes nest real <ul>/<ol>; newer imports use flat lists + ql-indent classes */
.note-html-content :deep(li > ul),
.note-html-content :deep(li > ol) {
  margin: 0;
  padding-left: var(--note-list-indent);
}

.note-html-content :deep(li) {
  padding-left: 0;
}

/* One indent step per tier, for both text blocks and list items */
.note-html-content :deep(.ql-indent-1:not(.ql-direction-rtl)) { --note-indent: calc(var(--note-indent-step) * 1); padding-left: calc(var(--note-indent-step) * 1); }
.note-html-content :deep(.ql-indent-2:not(.ql-direction-rtl)) { --note-indent: calc(var(--note-indent-step) * 2); padding-left: calc(var(--note-indent-step) * 2); }
.note-html-content :deep(.ql-indent-3:not(.ql-direction-rtl)) { --note-indent: calc(var(--note-indent-step) * 3); padding-left: calc(var(--note-indent-step) * 3); }
.note-html-content :deep(.ql-indent-4:not(.ql-direction-rtl)) { --note-indent: calc(var(--note-indent-step) * 4); padding-left: calc(var(--note-indent-step) * 4); }
.note-html-content :deep(.ql-indent-5:not(.ql-direction-rtl)) { --note-indent: calc(var(--note-indent-step) * 5); padding-left: calc(var(--note-indent-step) * 5); }
.note-html-content :deep(.ql-indent-6:not(.ql-direction-rtl)) { --note-indent: calc(var(--note-indent-step) * 6); padding-left: calc(var(--note-indent-step) * 6); }
.note-html-content :deep(.ql-indent-7:not(.ql-direction-rtl)) { --note-indent: calc(var(--note-indent-step) * 7); padding-left: calc(var(--note-indent-step) * 7); }
.note-html-content :deep(.ql-indent-8:not(.ql-direction-rtl)) { --note-indent: calc(var(--note-indent-step) * 8); padding-left: calc(var(--note-indent-step) * 8); }

.note-html-content :deep(p.note-hang-1:not(.ql-direction-rtl)) {
  padding-left: calc(var(--note-indent) + 0.82em);
  text-indent: -0.82em;
}
.note-html-content :deep(p.note-hang-2:not(.ql-direction-rtl)),
.note-html-content :deep(h3.note-hang-2:not(.ql-direction-rtl)),
.note-html-content :deep(h4.note-hang-2:not(.ql-direction-rtl)),
.note-html-content :deep(h5.note-hang-2:not(.ql-direction-rtl)) {
  padding-left: calc(var(--note-indent) + 1.22em);
  text-indent: -1.22em;
}

/* Raw HTML: no Quill overrides; only safe layout defaults */
.note-html-content :deep(img) {
  max-width: 100%;
  height: auto;
}

.note-html-content :deep(table),
.note-html-content :deep(.ql-note-table table) {
  max-width: 100%;
  width: 100%;
  border-collapse: collapse;
  margin: 0.4em 0;
}
.note-html-content :deep(table td),
.note-html-content :deep(table th),
.note-html-content :deep(.ql-note-table td),
.note-html-content :deep(.ql-note-table th) {
  border: 1px solid #000;
  padding: 6px 8px;
  vertical-align: middle;
}

.notes-nav-arrows {
  margin-top: 8px;
}

.questionoption-arrow--disabled {
  opacity: 0.35;
  cursor: not-allowed;
  pointer-events: none;
}

@media (min-width: 1200px) {
  .container {
    max-width: 1220px;
  }
}

</style>

